/**
 * A proposal as the builder holds it while somebody is writing one.
 *
 * One shape for a quote on a job and for a template in Settings, so the same
 * editor writes both. Lines name their package by the package's `key`, the
 * way the server's input does — for a saved quote the key is the option's id,
 * for a new package it is made up here — and the server allocates real ids
 * when it writes. Decision 80.
 *
 * Money stays integer minor units the whole way. A price field holds what was
 * typed as text; `unitPrice` is only ever set from `centsOf` of it.
 */
import {
  isProposalImage,
  proposalTotals,
  sectionFileIds,
  unitPriceIncTax,
  type ProposalTemplateContent,
  type QuoteSectionKindKey,
} from '@fieldops/shared'
import { fileUrl } from '../../lib/files.js'
import type { RouterOutputs } from '../../lib/trpc.js'
import type { ProposalView } from './ProposalPage.js'

export type QuoteDetail = RouterOutputs['quotes']['byId']

export interface DraftSection {
  key: string
  kind: QuoteSectionKindKey
  title: string
  body: string
  /** A cover's photograph. */
  attachmentId: string | null
  /** For the editor's thumbnail: the image's own record, when there is one. */
  image: { id: string; checksum: string | null } | null
  /** A photo block's photos and PDFs, in order. Decision 82. */
  files: DraftFile[]
}

export interface DraftFile {
  id: string
  checksum: string | null
  filename: string
  mimeType: string
}

type StoredFile = DraftFile

/** A photo block's files from the ids it was saved with, skipping any that are gone. */
function filesOf(
  section: { kind: QuoteSectionKindKey; attachmentId?: string | null; fileIds?: readonly string[] | null },
  stored: ReadonlyMap<string, StoredFile>,
): DraftFile[] {
  return sectionFileIds(section).flatMap((id) => {
    const file = stored.get(id)
    return file ? [file] : []
  })
}

export interface DraftOption {
  key: string
  name: string
  summary: string
  isRecommended: boolean
}

export interface DraftLine {
  key: string
  description: string
  quantity: string
  /** Minor units, ex tax. Null in a template means "price it for the client". */
  unitPrice: number | null
  /**
   * The price as typed inc tax, when it was typed that way. It is what the
   * line charges; `unitPrice` beside it is the nearest ex-tax cent. Cleared
   * by an ex-tax edit.
   */
  unitPriceIncTax: number | null
  taxRateId: string | null
  taxRateBasisPoints: number
  isOptional: boolean
  optionKey: string | null
  materialId: string | null
  serviceTypeId: string | null
  /** Where the line came from, for the label under it. */
  source: string | null
}

export interface ProposalDraft {
  title: string
  termsText: string
  /** A quote's date. */
  validUntil: string | null
  /** A template's lifetime. */
  validForDays: number | null
  /** Minor units off the subtotal. A quote's; a template has none. */
  discountAmount: number
  sections: DraftSection[]
  options: DraftOption[]
  lines: DraftLine[]
}

let counter = 0
export function newKey(prefix: string): string {
  counter += 1
  return `${prefix}_${Date.now().toString(36)}${counter}`
}

export const SECTION_LABEL: Record<QuoteSectionKindKey, string> = {
  COVER: 'Cover',
  TEXT: 'Text',
  IMAGE: 'Photos & files',
  PRICING: 'Pricing',
  TERMS: 'Terms',
}

export function blankSection(kind: QuoteSectionKindKey): DraftSection {
  const titles: Record<QuoteSectionKindKey, string> = {
    COVER: '',
    TEXT: '',
    IMAGE: '',
    PRICING: 'Your options',
    TERMS: 'Terms and conditions',
  }
  return { key: newKey('s'), kind, title: titles[kind], body: '', attachmentId: null, image: null, files: [] }
}

export function blankLine(
  defaults: { taxRateId: string | null; taxRateBasisPoints: number },
  patch: Partial<DraftLine> = {},
): DraftLine {
  return {
    key: newKey('l'),
    description: '',
    quantity: '1',
    unitPrice: 0,
    unitPriceIncTax: null,
    taxRateId: defaults.taxRateId,
    taxRateBasisPoints: defaults.taxRateBasisPoints,
    isOptional: false,
    optionKey: null,
    materialId: null,
    serviceTypeId: null,
    source: null,
    ...patch,
  }
}

/** A new proposal: a cover, a page of words, and where the price goes. */
export function starterDraft(title = ''): ProposalDraft {
  return {
    title,
    termsText: '',
    validUntil: null,
    validForDays: 30,
    discountAmount: 0,
    sections: [blankSection('COVER'), blankSection('TEXT'), blankSection('PRICING')],
    options: [],
    lines: [],
  }
}

/**
 * A plain quote as the start of a proposal. Its lines are untouched — they
 * are the included line items the pricing block draws — so what the quote
 * said and what the proposal's fixed items say are the same rows.
 */
export function asProposal(draft: ProposalDraft): ProposalDraft {
  return { ...draft, sections: starterDraft().sections }
}

export function draftFromQuote(quote: QuoteDetail): ProposalDraft {
  const stored = new Map(quote.sectionFiles.map((file) => [file.id, file]))
  return {
    title: quote.title ?? '',
    termsText: quote.termsText ?? '',
    validUntil: quote.validUntil ? new Date(quote.validUntil).toISOString().slice(0, 10) : null,
    validForDays: null,
    discountAmount: quote.discountAmount,
    sections: quote.sections.map((section) => ({
      key: section.id,
      kind: section.kind,
      title: section.title ?? '',
      body: section.body ?? '',
      attachmentId: section.kind === 'IMAGE' ? null : section.attachmentId,
      image:
        section.kind !== 'IMAGE' && section.attachment
          ? { id: section.attachment.id, checksum: section.attachment.checksum }
          : null,
      files: filesOf(section, stored),
    })),
    options: quote.options.map((option) => ({
      key: option.id,
      name: option.name,
      summary: option.summary ?? '',
      isRecommended: option.isRecommended,
    })),
    lines: quote.lines.map((line) => ({
      key: line.id,
      description: line.description,
      quantity: quantityText(line.quantity),
      unitPrice: line.unitPrice,
      unitPriceIncTax: line.unitPriceIncTax ?? null,
      taxRateId: line.taxRateId,
      taxRateBasisPoints: line.taxRateBasisPoints,
      isOptional: line.isOptional,
      optionKey: line.optionId,
      materialId: line.materialId,
      serviceTypeId: line.serviceTypeId,
      source: line.material?.name && line.material.name !== line.description ? line.material.name : null,
    })),
  }
}

export function draftFromTemplate(
  content: ProposalTemplateContent,
  rates: Map<string, number>,
  files: readonly StoredFile[] = [],
): ProposalDraft {
  const stored = new Map(files.map((file) => [file.id, file]))
  return {
    title: content.title ?? '',
    termsText: content.termsText ?? '',
    validUntil: null,
    validForDays: content.validForDays ?? null,
    discountAmount: 0,
    sections: content.sections.map((section) => ({
      key: newKey('s'),
      kind: section.kind,
      title: section.title ?? '',
      body: section.body ?? '',
      attachmentId: section.kind === 'IMAGE' ? null : (section.attachmentId ?? null),
      // A template keeps only the id. It was an upload through `POST /files`,
      // which writes the row only once the bytes are stored.
      image:
        section.kind !== 'IMAGE' && section.attachmentId ? { id: section.attachmentId, checksum: 'stored' } : null,
      files: filesOf(section, stored),
    })),
    options: content.options.map((option) => ({
      key: option.key,
      name: option.name,
      summary: option.summary ?? '',
      isRecommended: option.isRecommended,
    })),
    lines: content.lines.map((line) => ({
      key: newKey('l'),
      description: line.description,
      quantity: String(line.quantity),
      unitPrice: line.unitPrice ?? null,
      unitPriceIncTax: line.unitPrice == null ? null : (line.unitPriceIncTax ?? null),
      taxRateId: line.taxRateId ?? null,
      taxRateBasisPoints: line.taxRateId ? (rates.get(line.taxRateId) ?? 0) : 0,
      isOptional: line.isOptional,
      optionKey: line.optionKey ?? null,
      materialId: line.materialId ?? null,
      serviceTypeId: line.serviceTypeId ?? null,
      source: null,
    })),
  }
}

/** The lines a save may send: a line with no words is a row being typed. */
function usableLines(draft: ProposalDraft): DraftLine[] {
  const keys = new Set(draft.options.map((option) => option.key))
  return draft.lines
    .filter((line) => line.description.trim() !== '')
    // A line left pointing at a package that was deleted falls back to
    // "included" rather than being refused on save.
    .map((line) => (line.optionKey && !keys.has(line.optionKey) ? { ...line, optionKey: null } : line))
}

function sectionsInput(draft: ProposalDraft) {
  return draft.sections.map((section) => ({
    kind: section.kind,
    title: section.title,
    body: section.body,
    attachmentId: section.kind === 'IMAGE' ? null : section.attachmentId,
    fileIds: section.kind === 'IMAGE' ? section.files.map((file) => file.id) : [],
  }))
}

function optionsInput(draft: ProposalDraft) {
  return draft.options
    .filter((option) => option.name.trim() !== '')
    .map((option) => ({
      key: option.key,
      name: option.name.trim(),
      summary: option.summary,
      isRecommended: option.isRecommended,
    }))
}

/** What `quotes.update` takes. */
export function quoteInput(draft: ProposalDraft) {
  const options = optionsInput(draft)
  const named = new Set(options.map((option) => option.key))

  return {
    title: draft.title.trim() === '' ? null : draft.title.trim(),
    termsText: draft.termsText.trim() === '' ? null : draft.termsText,
    validUntil: draft.validUntil ? new Date(`${draft.validUntil}T23:59:59`) : null,
    discountAmount: draft.discountAmount,
    sections: sectionsInput(draft),
    options,
    lines: usableLines(draft).map((line, index) => ({
      description: line.description.trim(),
      quantity: line.quantity.trim() === '' ? '1' : line.quantity.trim(),
      unitPrice: line.unitPrice ?? 0,
      unitPriceIncTax: line.unitPriceIncTax,
      unitCost: 0,
      discountBasisPoints: 0,
      taxRateId: line.taxRateId,
      isOptional: line.isOptional,
      sortOrder: index,
      optionKey: line.optionKey && named.has(line.optionKey) ? line.optionKey : null,
      materialId: line.materialId,
      serviceTypeId: line.serviceTypeId,
    })),
  }
}

/** What a template stores. */
export function templateContent(draft: ProposalDraft): ProposalTemplateContent {
  const options = optionsInput(draft)
  const named = new Set(options.map((option) => option.key))

  return {
    title: draft.title.trim() === '' ? null : draft.title.trim(),
    termsText: draft.termsText.trim() === '' ? null : draft.termsText,
    validForDays: draft.validForDays,
    sections: sectionsInput(draft).map((section) => ({
      ...section,
      title: section.title.trim() === '' ? null : section.title,
      body: section.body.trim() === '' ? null : section.body,
    })),
    options: options.map((option) => ({
      ...option,
      summary: option.summary.trim() === '' ? null : option.summary,
    })),
    lines: usableLines(draft).map((line) => ({
      optionKey: line.optionKey && named.has(line.optionKey) ? line.optionKey : null,
      description: line.description.trim(),
      quantity: line.quantity.trim() === '' ? '1' : line.quantity.trim(),
      unitPrice: line.unitPrice,
      unitPriceIncTax: line.unitPrice === null ? null : line.unitPriceIncTax,
      materialId: line.materialId,
      serviceTypeId: line.serviceTypeId,
      taxRateId: line.taxRateId,
      isOptional: line.isOptional,
    })),
  }
}

/**
 * A line's figures while it is being typed. The same half-up integer
 * arithmetic as `money.calculateLine`, so the builder and the saved quote
 * agree to the cent.
 */
export function lineFigures(line: DraftLine) {
  const quantity = Number(line.quantity.trim() === '' ? '1' : line.quantity)
  if (!Number.isFinite(quantity) || line.unitPrice === null) {
    return { lineTotalExTax: 0, lineTax: 0, lineTotalIncTax: 0 }
  }

  if (line.unitPriceIncTax != null) {
    const inc = Math.round((Math.round(quantity * 10_000) * line.unitPriceIncTax) / 10_000)
    const exOfInc = Math.round((inc * 10_000) / (10_000 + line.taxRateBasisPoints))
    return { lineTotalExTax: exOfInc, lineTax: inc - exOfInc, lineTotalIncTax: inc }
  }

  const ex = Math.round((Math.round(quantity * 10_000) * line.unitPrice) / 10_000)
  const tax = Math.round((ex * line.taxRateBasisPoints) / 10_000)
  return { lineTotalExTax: ex, lineTax: tax, lineTotalIncTax: ex + tax }
}

/** A price typed inc tax: kept as the figure, with its nearest ex-tax cent beside it. */
export function incTaxPrice(
  incTax: number | null,
  taxRateBasisPoints: number,
): Pick<DraftLine, 'unitPrice' | 'unitPriceIncTax'> {
  return incTax === null
    ? { unitPrice: null, unitPriceIncTax: null }
    : { unitPrice: exTaxFromIncTax(incTax, taxRateBasisPoints), unitPriceIncTax: incTax }
}

/** Every package's price, and the default, from the draft as it stands. */
export function draftTotals(draft: ProposalDraft) {
  return proposalTotals({
    lines: draft.lines
      .filter((line) => line.description.trim() !== '')
      .map((line) => ({ id: line.key, optionId: line.optionKey, isOptional: line.isOptional, ...lineFigures(line) })),
    options: draft.options.map((option, index) => ({
      id: option.key,
      isRecommended: option.isRecommended,
      sortOrder: index,
    })),
    discountAmount: draft.discountAmount,
  })
}

/** Dollars as typed to integer minor units. Null for blank. */
export function centsOf(text: string): number | null {
  const trimmed = text.trim().replace(/[$,\s]/g, '')
  if (trimmed === '') return null
  const value = Number(trimmed)
  return Number.isFinite(value) ? Math.round(value * 100) : null
}

/**
 * The ex-tax unit price for a price typed inc GST. Of the cents either side of
 * the plain division it takes one that `unitPriceIncTax` turns back into
 * exactly what was typed, so $245.00 does not come back as $244.99.
 */
export function exTaxFromIncTax(incTax: number, taxRateBasisPoints: number): number {
  const guess = Math.round((incTax * 10_000) / (10_000 + taxRateBasisPoints))
  return (
    [guess, guess - 1, guess + 1].find(
      (unitPrice) => unitPriceIncTax({ unitPrice, taxRateBasisPoints }) === incTax,
    ) ?? guess
  )
}

export function dollarsOf(cents: number | null): string {
  if (cents === null) return ''
  return (cents / 100).toFixed(2).replace(/\.00$/, '')
}

export interface ProposalMeta {
  number: string
  version: number
  customerName: string
  siteAddress: string | null
  jobNumber: string
  date: Date | string
}

/** The draft as the customer's page reads a proposal, for drawing it live. */
export function draftAsView(draft: ProposalDraft, meta: ProposalMeta): ProposalView {
  const validOptions = new Set(draft.options.map((option) => option.key))

  return {
    number: meta.number,
    title: draft.title,
    version: meta.version,
    jobNumber: meta.jobNumber,
    date: new Date(meta.date),
    validUntil: draft.validUntil
      ? new Date(`${draft.validUntil}T23:59:59`)
      : draft.validForDays
        ? new Date(Date.now() + draft.validForDays * 86_400_000)
        : null,
    customerName: meta.customerName,
    siteAddress: meta.siteAddress,
    termsText: draft.termsText || null,
    discountAmount: draft.discountAmount,
    sections: draft.sections.map((section) => ({
      id: section.key,
      kind: section.kind,
      title: section.title || null,
      body: section.body || null,
      imageUrl: section.image ? fileUrl(section.image) : null,
      imageWidth: null,
      imageHeight: null,
      files: section.files.flatMap((file) => {
        const url = fileUrl(file)
        return url
          ? [{ id: file.id, url, filename: file.filename, isImage: isProposalImage(file.mimeType), width: null, height: null }]
          : []
      }),
    })),
    options: draft.options.map((option, index) => ({
      id: option.key,
      name: option.name || 'Unnamed option',
      summary: option.summary || null,
      isRecommended: option.isRecommended,
      sortOrder: index,
    })),
    lines: draft.lines
      .filter((line) => line.description.trim() !== '')
      .map((line) => ({
        id: line.key,
        description: line.description,
        quantity: line.quantity || '1',
        unitPrice: line.unitPrice ?? 0,
        unitPriceIncTax: line.unitPriceIncTax,
        taxRateBasisPoints: line.taxRateBasisPoints,
        ...lineFigures(line),
        optionId: line.optionKey && validOptions.has(line.optionKey) ? line.optionKey : null,
        isOptional: line.isOptional,
      })),
  }
}

function quantityText(quantity: { toString(): string }): string {
  const text = quantity.toString()
  const value = Number(text)
  return Number.isFinite(value) ? String(value) : text
}
